import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";

interface SellerFormData {
  productType: string;
  qualityGrade: string;
  availableQuantity: string;
  expectedRate: string;
  village: string;
  districtState: string;
  sellerName: string;
  mobileNumber: string;
  sellerType: string;
}

const ForSellers = () => {
  const [formData, setFormData] = useState<SellerFormData>({
    productType: "",
    qualityGrade: "",
    availableQuantity: "",
    expectedRate: "",
    village: "",
    districtState: "",
    sellerName: "",
    mobileNumber: "",
    sellerType: "",
  });

  const [errors, setErrors] = useState<Partial<SellerFormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<SellerFormData> = {};

    if (!formData.productType.trim()) {
      newErrors.productType = "Product type is required";
    }
    if (!formData.qualityGrade.trim()) {
      newErrors.qualityGrade = "Quality/Grade is required";
    }
    if (!formData.availableQuantity.trim()) {
      newErrors.availableQuantity = "Available quantity is required";
    }
    if (!formData.expectedRate.trim()) {
      newErrors.expectedRate = "Expected rate is required";
    }
    if (!formData.village.trim()) {
      newErrors.village = "Village/Market yard is required";
    }
    if (!formData.districtState.trim()) {
      newErrors.districtState = "District & State is required";
    }
    if (!formData.sellerName.trim()) {
      newErrors.sellerName = "Seller name is required";
    }
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Invalid mobile number (10 digits required)";
    }
    if (!formData.sellerType) {
      newErrors.sellerType = "Seller type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const message = `Seller Submission – VIATRA

Product: ${formData.productType}
Quality: ${formData.qualityGrade}
Quantity: ${formData.availableQuantity}
Expected Rate: ${formData.expectedRate}
Location: ${formData.village}, ${formData.districtState}
Seller Name: ${formData.sellerName}
Mobile: ${formData.mobileNumber}
Seller Type: ${formData.sellerType}`;

    const whatsappUrl = `https://wa.me/919327264950?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <Layout>
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Page Title */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Sell Your Produce with Confidence
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              VIATRA helps farmers and traders sell their produce at the right price. Submit your product details below and our team will contact you directly via WhatsApp.
            </p>
          </div>

          {/* How It Works - 3 steps */}
          <section className="mb-12">
            <Card className="p-6 md:p-8 border-primary/10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <h4 className="text-lg font-semibold text-foreground">1. Submit Details</h4>
                  <p className="text-sm text-muted-foreground mt-2">Fill in your product information and expected price.</p>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-foreground">2. Verification by VIATRA</h4>
                  <p className="text-sm text-muted-foreground mt-2">Our team checks quality, quantity, and market demand.</p>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-foreground">3. Buyer Connection</h4>
                  <p className="text-sm text-muted-foreground mt-2">We connect you with genuine buyers and traders.</p>
                </div>
              </div>
            </Card>
          </section>

          {/* WHO CAN SELL - moved up for scannability */}
          <section className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <Card className="p-4 text-center">
                <p className="font-semibold text-foreground">Farmers</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="font-semibold text-foreground">Wholesale Traders</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="font-semibold text-foreground">Commission Agents</p>
              </Card>
              <Card className="p-4 text-center">
                <p className="font-semibold text-foreground">Aggregators</p>
              </Card>
            </div>
            <p className="text-sm text-muted-foreground">Individuals and businesses involved in agricultural trade are welcome.</p>
          </section>

          {/* WHAT YOU NEED - separate card layout with visual hierarchy */}
          <section className="mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-6">What You Need to Sell on VIATRA</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Required Card - Primary (Higher Visual Priority) */}
              <Card className="p-6 md:p-8 border-primary/20 border-2">
                <h4 className="text-base font-semibold text-foreground mb-4">Required</h4>
                <ul className="space-y-3 text-sm text-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold flex-shrink-0">✓</span>
                    <span>Agricultural produce ready for sale</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold flex-shrink-0">✓</span>
                    <span>Clear information about quality and quantity</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold flex-shrink-0">✓</span>
                    <span>Location (village / market yard)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary font-bold flex-shrink-0">✓</span>
                    <span>A WhatsApp-enabled mobile number</span>
                  </li>
                </ul>
                <p className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground">
                  No license or registration is required for farmers.
                </p>
              </Card>

              {/* Optional Card - Secondary (Lower Visual Priority) */}
              <Card className="p-6 md:p-8 border-border">
                <h4 className="text-base font-semibold text-foreground mb-4">Optional</h4>
                <ul className="space-y-3 text-sm text-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground flex-shrink-0">◦</span>
                    <span>GST number (for large traders / exporters)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground flex-shrink-0">◦</span>
                    <span>Transport availability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-muted-foreground flex-shrink-0">◦</span>
                    <span>Packaging details</span>
                  </li>
                </ul>
              </Card>
            </div>
          </section>

          {/* Section: Seller Submission Form - give visual priority */}
          <section className="mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Submit Your Produce</h2>
            <Card className="p-6 md:p-8 border-primary/20 bg-secondary/5">
              <div className="space-y-6">
                {/* A. Product Information */}
                <div className="pb-6 border-b border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-2">A. Product Information</h3>
                  <p className="text-sm text-muted-foreground mb-4">Product details help us understand your produce clearly.</p>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-foreground mb-2">Product Type <span className="text-red-500">*</span></label>
                    <Select value={formData.productType} onValueChange={(val) => setFormData({ ...formData, productType: val })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Onion">Onion</SelectItem>
                        <SelectItem value="Potato">Potato</SelectItem>
                        <SelectItem value="Tomato">Tomato</SelectItem>
                        <SelectItem value="Wheat">Wheat</SelectItem>
                        <SelectItem value="Rice">Rice</SelectItem>
                        <SelectItem value="Garlic">Garlic</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.productType && <p className="text-xs text-red-500 mt-1">{errors.productType}</p>}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-foreground mb-2">Quality / Grade <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="e.g., A / B / Export"
                      value={formData.qualityGrade}
                      onChange={(e) => setFormData({ ...formData, qualityGrade: e.target.value })}
                      className="w-full"
                    />
                    {errors.qualityGrade && <p className="text-xs text-red-500 mt-1">{errors.qualityGrade}</p>}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-foreground mb-2">Available Quantity <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="e.g., 5000 kg or 5 MT"
                      value={formData.availableQuantity}
                      onChange={(e) => setFormData({ ...formData, availableQuantity: e.target.value })}
                      className="w-full"
                    />
                    {errors.availableQuantity && <p className="text-xs text-red-500 mt-1">{errors.availableQuantity}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">Expected Rate (₹ per kg) <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="e.g., 45 (specify unit if needed)"
                      value={formData.expectedRate}
                      onChange={(e) => setFormData({ ...formData, expectedRate: e.target.value })}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Enter your expected selling price</p>
                    {errors.expectedRate && <p className="text-xs text-red-500 mt-1">{errors.expectedRate}</p>}
                  </div>
                </div>

                {/* B. Location Details */}
                <div className="pb-6 border-b border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-2">B. Location Details</h3>
                  <p className="text-sm text-muted-foreground mb-4">Where the produce is located helps buyers estimate logistics.</p>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-foreground mb-2">Village / Market Yard <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="e.g., Nashik, Lasalgaon"
                      value={formData.village}
                      onChange={(e) => setFormData({ ...formData, village: e.target.value })}
                      className="w-full"
                    />
                    {errors.village && <p className="text-xs text-red-500 mt-1">{errors.village}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">District & State <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="e.g., Nashik, Maharashtra"
                      value={formData.districtState}
                      onChange={(e) => setFormData({ ...formData, districtState: e.target.value })}
                      className="w-full"
                    />
                    {errors.districtState && <p className="text-xs text-red-500 mt-1">{errors.districtState}</p>}
                  </div>
                </div>

                {/* C. Seller Details */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">C. Seller Details</h3>
                  <p className="text-sm text-muted-foreground mb-4">Help us reach you quickly about this enquiry.</p>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-foreground mb-2">Seller Name <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="Your full name"
                      value={formData.sellerName}
                      onChange={(e) => setFormData({ ...formData, sellerName: e.target.value })}
                      className="w-full"
                    />
                    {errors.sellerName && <p className="text-xs text-red-500 mt-1">{errors.sellerName}</p>}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-foreground mb-2">Mobile Number (WhatsApp) <span className="text-red-500">*</span></label>
                    <Input
                      placeholder="10-digit mobile number"
                      value={formData.mobileNumber}
                      onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground mt-1">We will contact you only regarding this enquiry.</p>
                    {errors.mobileNumber && <p className="text-xs text-red-500 mt-1">{errors.mobileNumber}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">Seller Type <span className="text-red-500">*</span></label>
                    <Select value={formData.sellerType} onValueChange={(value) => setFormData({ ...formData, sellerType: value })}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select your category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Farmer">Farmer</SelectItem>
                        <SelectItem value="Trader">Wholesale Trader</SelectItem>
                        <SelectItem value="Commission Agent">Commission Agent</SelectItem>
                        <SelectItem value="Aggregator">Aggregator</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.sellerType && <p className="text-xs text-red-500 mt-1">{errors.sellerType}</p>}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    onClick={handleSubmit}
                    className="w-full bg-primary hover:bg-primary/90 text-white text-lg py-6 rounded-lg font-semibold transition-colors glow-gold"
                  >
                    Submit Details via WhatsApp
                  </Button>
                  <p className="text-sm text-muted-foreground text-center mt-3">🔒 Your information is safe and shared only with verified buyers through VIATRA.</p>
                  <p className="text-xs text-muted-foreground text-center mt-2">⏱ Typical response time: 24 hours</p>
                </div>
              </div>
            </Card>
          </section>

          {/* What Happens After You Submit? - numbered timeline */}
          <section className="mb-8">
            <h3 className="text-lg font-semibold text-foreground mb-4">What Happens After You Submit?</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">1</div>
                <div>
                  <p className="font-semibold text-foreground">Details received on WhatsApp</p>
                  <p className="text-sm text-muted-foreground">Your submission reaches the VIATRA team instantly.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">2</div>
                <div>
                  <p className="font-semibold text-foreground">Verification & discussion</p>
                  <p className="text-sm text-muted-foreground">We may contact you to confirm quality, pricing, and availability.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">3</div>
                <div>
                  <p className="font-semibold text-foreground">Buyer matching</p>
                  <p className="text-sm text-muted-foreground">Your produce is matched with genuine buyers based on demand.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">4</div>
                <div>
                  <p className="font-semibold text-foreground">Deal support</p>
                  <p className="text-sm text-muted-foreground">VIATRA assists in coordination and next steps.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Data Protection & Verification Notice - final bordered container */}
          <section className="mb-12">
            <Card className="p-6 md:p-8 border-primary/20">
              <h4 className="text-lg font-semibold text-foreground mb-3">🔒 Your data is protected</h4>
              <p className="text-sm text-muted-foreground">VIATRA does not publicly share seller contact details. Information is shared only with verified buyers.</p>
              <div className="mt-4">
                <p className="font-semibold">✓ Verification Process:</p>
                <p className="text-sm text-muted-foreground">Submitted details will be reviewed and verified by the VIATRA team before listing. Your contact information remains confidential and is only shared with genuine buyers.</p>
              </div>
            </Card>
          </section>

          {/* IMPORTANT NOTE: UX/content-only update */}
          <section className="mt-6">
            <p className="text-xs text-muted-foreground text-center">This update is a UX/content improvement only. Visual theme and layout structure must remain unchanged.</p>
          </section>
        </div>
      </main>
    </Layout>
  );
};

export default ForSellers;
